import SPCountercharge from "../scripts/SPAbilities/Countercharge";
import SPOverchargedShot from "../scripts/SPAbilities/OverchargedShot";
import RegenCore from "../scripts/SPAbilities/RegenCore";
import SPBullet from "../scripts/SPBullet";
import SPDash from "../scripts/SPAbilities/SPDash";
import SPGameManager from "../scripts/SPGameManager";
import SPGun from "../scripts/SPGun";
import { SPHealZone } from "../scripts/SPAbilities/SPHealZone";
import SPPlayer from "../scripts/SPPlayer";
import SPRocketGun from "../scripts/SPRocketGun";
import SPScanner from "../scripts/SPScanner";
import { Random } from "../util/Random";
import QuantumJump from "../scripts/SPAbilities/QuantumJump";
import Berserk from "../scripts/SPAbilities/Berserk";
import EmpBlast from "../scripts/SPAbilities/EmpBlast";
import Hypercharge from "../scripts/SPAbilities/Hypercharge";
import PlasmaWave from "../scripts/SPAbilities/PlasmaWave";
import { ContainsNone } from "../util";
import GravitonPulse from "../scripts/SPAbilities/GravitonPulse";
import HeavyBurstCannon from "../scripts/SPGuns/HeavyBurstCannon";
import ShockwaveLauncher from "../scripts/SPGuns/ShockwaveLauncher";
import ForceField from "../scripts/SPAbilities/ForceField";

export enum PerkType {
    Weapon = "Weapon",
    Ability = "Ability",
    Passive = "Passive Ability"
}

export type Perk = {
    id: string;
    type: PerkType;
    name: string;
    description: string;
    icon: number;
    callback: () => void;
    onLose: () => void;
    requirements: {
        level: {
            min: number;
            max: number;
        };
        perkId?: string;
        excludePerkIds?: string[];
    };
};

export const PERKS: Perk[] = [
    // Tier 1 Weapons
    {
        id: "blaster_cannon",
        type: PerkType.Weapon,
        name: "Blaster Cannon",
        description: "Balanced damage with a moderate fire rate.",
        icon: 0,
        callback: () => {
            SPPlayer.Singleton.gun = new SPGun(SPPlayer.Singleton, {
                bulletSpeed: 200,
                damage: 1,
                fireRate: 3,
                magSize: 30,
                mouseTrigger: 0,
                reloadTime: 1,
                maxEnemiesHit: 1
            });
        },
        onLose: () => {},
        requirements: { level: { min: 1, max: 4 } }
    },
    {
        id: "rapid_pulse_gun",
        type: PerkType.Weapon,
        name: "Rapid Pulse Gun",
        description: "Fast fire rate with low damage per shot.",
        icon: 0,
        callback: () => {
            SPPlayer.Singleton.gun = new SPGun(SPPlayer.Singleton, {
                bulletSpeed: 200,
                damage: 0.6,
                fireRate: 6,
                magSize: 25,
                mouseTrigger: 0,
                reloadTime: 0.75,
                maxEnemiesHit: 1
            });
        },
        onLose: () => {},
        requirements: { level: { min: 1, max: 4 } }
    },
    {
        id: "piercing_railgun",
        type: PerkType.Weapon,
        name: "Piercing Railgun",
        description: "High damage, slow fire rate, pierces enemies.",
        icon: 0,
        callback: () => {
            SPPlayer.Singleton.gun = new SPGun(SPPlayer.Singleton, {
                bulletSpeed: 600,
                damage: 4,
                fireRate: 1,
                magSize: 8,
                mouseTrigger: 0,
                reloadTime: 1.5,
                maxEnemiesHit: 2
            });
        },
        onLose: () => {},
        requirements: { level: { min: 1, max: 4 } }
    },

    // Tier 2 Weapons
    {
        id: "missile_barrage_pod",
        type: PerkType.Weapon,
        name: "Missile Barrage Pod",
        description: "Fires homing missiles with limited ammo.",
        icon: 0,
        callback: () => {
            SPPlayer.Singleton.secondaryGun = new SPRocketGun(SPPlayer.Singleton);
        },
        onLose: () => {
            SPPlayer.Singleton.secondaryGun = undefined;
        },
        requirements: { level: { min: 5, max: 10 } }
    },
    {
        id: "heavy_burst_cannon",
        type: PerkType.Weapon,
        name: "Heavy Burst Cannon",
        description: "Wide spread, strong but slow.",
        icon: 0,
        callback: () => {
            SPPlayer.Singleton.secondaryGun = new HeavyBurstCannon(SPPlayer.Singleton);
        },
        onLose: () => {
            SPPlayer.Singleton.secondaryGun = undefined;
        },
        requirements: { level: { min: 5, max: 10 } }
    },
    {
        id: "shockwave_launcher",
        type: PerkType.Weapon,
        name: "Shockwave Launcher",
        description: "Short-range AOE burst with cooldown.",
        icon: 0,
        callback: () => {
            SPPlayer.Singleton.secondaryGun = new ShockwaveLauncher(SPPlayer.Singleton);
        },
        onLose: () => {
            SPPlayer.Singleton.secondaryGun = undefined;
        },
        requirements: { level: { min: 5, max: 10 } }
    },

    // Tier 3 Weapons
    // {
    //     id: "void_beam",
    //     type: PerkType.Weapon,
    //     name: "Void Beam",
    //     description: "Continuous laser that drains power while active.",
    //     icon: 0,
    //     callback: () => {},
    //     onLose: () => {},
    //     requirements: { level: { min: 11, max: 15 } }
    // },
    {
        id: "arc_blaster",
        type: PerkType.Weapon,
        name: "Arc Blaster",
        description: "Chain lightning effect hitting multiple enemies.",
        icon: 0,
        callback: () => {
            SPPlayer.Singleton.gun.arcBlaster = true;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.arcBlaster = false;
        },
        requirements: { level: { min: 11, max: 15 } }
    },

    // Tier 4 Weapons
    {
        id: "omega_cannon",
        type: PerkType.Weapon,
        name: "Omega Cannon",
        description: "Massive energy shot with global-range piercing.",
        icon: 0,
        callback: () => {},
        onLose: () => {},
        requirements: { level: { min: 16, max: 99 } }
    },
    {
        id: "dark_matter_projector",
        type: PerkType.Weapon,
        name: "Dark Matter Projector",
        description: "Creates a singularity that pulls enemies inward.",
        icon: 0,
        callback: () => {},
        onLose: () => {},
        requirements: { level: { min: 16, max: 99 } }
    },
    {
        id: "starfire_lance",
        type: PerkType.Weapon,
        name: "Starfire Lance",
        description: "Infinite-range beam dealing scaling damage over time.",
        icon: 0,
        callback: () => {},
        onLose: () => {},
        requirements: { level: { min: 16, max: 99 } }
    },

    // Abilities
    {
        id: "afterburner_dash",
        type: PerkType.Ability,
        name: "Afterburner Dash",
        description: "Quick burst of speed to evade attacks.",
        icon: 9,
        callback: () => {
            // SPPlayer.Singleton.gameObject.AddScript(SPDash);
            SPPlayer.Singleton.AddAbility(new SPDash(SPPlayer.Singleton));
        },
        onLose: () => {
            // SPPlayer.Singleton.gameObject.RemoveScript(SPDash);
            SPPlayer.Singleton.RemoveAbility(SPDash);
        },
        requirements: { level: { min: 2, max: 5 } }
    },
    {
        id: "scanner",
        type: PerkType.Passive,
        name: "Scanner",
        description: "Displays enemy health bars",
        icon: 10,
        callback: () => {
            SPPlayer.Singleton.gameObject.AddScript(SPScanner);
        },
        onLose: () => {
            SPPlayer.Singleton.gameObject.RemoveScript(SPScanner);
        },
        requirements: { level: { min: 2, max: 11 } }
    },
    {
        id: "healing_field",
        type: PerkType.Ability,
        name: "Healing Field",
        description: "Creates an AOE healing zone.",
        icon: 12,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new SPHealZone(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(SPHealZone);
        },
        requirements: { level: { min: 2, max: 5 } }
    },
    {
        id: "regenerative_core",
        type: PerkType.Passive,
        name: "Regenerative Core",
        description: "Slowly regenerates lives over time",
        icon: 12,
        callback: () => {
            SPPlayer.Singleton.gameObject.AddScript(RegenCore);
        },
        onLose: () => {
            SPPlayer.Singleton.gameObject.RemoveScript(RegenCore);
        },
        requirements: { level: { min: 2, max: 10 } }
    },
    {
        id: "overcharged_shot",
        type: PerkType.Ability,
        name: "Overcharged Shot",
        description: "Fires a powerful charged shot dealing massive damage.",
        icon: 8,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new SPOverchargedShot(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(SPOverchargedShot);
        },
        requirements: { level: { min: 2, max: 5 } }
    },
    {
        id: "countercharge",
        type: PerkType.Ability,
        name: "Countercharge",
        description: "Reflects a portion of damage back to attackers.",
        icon: 5,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new SPCountercharge(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(SPCountercharge);
        },
        requirements: { level: { min: 3, max: 10 } }
    },
    // {
    //     id: "decoy_drone",
    //     type: PerkType.Ability,
    //     name: "Decoy Drone",
    //     description: "Deploys a drone that distracts and absorbs enemy fire.",
    //     icon: 11,
    //     callback: () => {},
    //     onLose: () => {},
    //     requirements: { level: { min: 4, max: 10 } }
    // },
    // {
    //     id: "shield_overcharge",
    //     type: PerkType.Ability,
    //     name: "Shield Overcharge",
    //     description: "Temporarily boosts shield capacity for added defense.",
    //     icon: 3,
    //     callback: () => {},
    //     onLose: () => {},
    //     requirements: { level: { min: 5, max: 10 } }
    // },
    {
        id: "emp_blast",
        type: PerkType.Passive,
        name: "EMP Blast",
        description: "Deals periodic AoE damage around the player",
        icon: 1,
        callback: () => {
            SPPlayer.Singleton.gameObject.AddScript(EmpBlast);
        },
        onLose: () => {
            SPPlayer.Singleton.gameObject.RemoveScript(EmpBlast);
        },
        requirements: { level: { min: 6, max: 18 } }
    },
    {
        id: "extra_life",
        type: PerkType.Passive,
        name: "Extra Life",
        description: "Take 1 more hit of damage before dying",
        icon: 2,
        callback: () => {
            SPPlayer.Singleton.maxHP++;
            SPPlayer.Singleton.hp++;
        },
        onLose: () => {
            SPPlayer.Singleton.maxHP--;
            if (SPPlayer.Singleton.hp > SPPlayer.Singleton.maxHP) SPPlayer.Singleton.hp = SPPlayer.Singleton.maxHP;
        },
        requirements: { level: { min: 6, max: 18 } }
    },
    {
        id: "extra_life_2",
        type: PerkType.Passive,
        name: "Extra Life",
        description: "Take 1 more hit of damage before dying",
        icon: 2,
        callback: () => {
            SPPlayer.Singleton.maxHP++;
            SPPlayer.Singleton.hp++;
        },
        onLose: () => {
            SPPlayer.Singleton.maxHP--;
            if (SPPlayer.Singleton.hp > SPPlayer.Singleton.maxHP) SPPlayer.Singleton.hp = SPPlayer.Singleton.maxHP;
        },
        requirements: { level: { min: 14, max: 20 } }
    },
    {
        id: "ignition",
        type: PerkType.Passive,
        name: "Ignition",
        description: "Increases fire rate.",
        icon: 1,
        callback: () => {
            SPPlayer.Singleton.gun.fireRate++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.fireRate--;
        },
        requirements: { level: { min: 6, max: 12 } }
    },
    {
        id: "ignition_2",
        type: PerkType.Passive,
        name: "Ignition",
        description: "Increases fire rate.",
        icon: 1,
        callback: () => {
            SPPlayer.Singleton.gun.fireRate++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.fireRate--;
        },
        requirements: { level: { min: 12, max: 18 } }
    },
    {
        id: "ignition_3",
        type: PerkType.Passive,
        name: "Ignition",
        description: "Increases fire rate.",
        icon: 1,
        callback: () => {
            SPPlayer.Singleton.gun.fireRate++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.fireRate--;
        },
        requirements: { level: { min: 17, max: 30 } }
    },
    {
        id: "tri_shot",
        type: PerkType.Passive,
        name: "Tri Shot",
        description: "[PERMANENT] Shoot 3 bullets",
        icon: 1,
        callback: () => {
            const ang = 0.2618;
            const player = SPPlayer.Singleton;
            player.gun.onShoot = () => {
                const v1 = player.transform.rotation.rotate(-ang/2);
                const v2 = player.transform.rotation.rotate(ang/2);

                for (const v of [v1, player.transform.forward, v2]) {
                    player.engine.NewGameObjectWithScript(SPBullet, {
                        position: player.transform.position.add(v.scale(player.size + 3)),
                        initialValues: {
                            damage: player.gun.damage,
                            velocity: player.velocity.add(v.scale(player.gun.bulletSpeed)),
                            maxEnemiesHit: player.gun.maxEnemiesHit,
                            arcBlaster: player.gun.arcBlaster
                        }
                    });
                }
            }
        },
        onLose: () => {},
        requirements: { 
            level: { min: 6, max: 12 },
            excludePerkIds: [
                "double_barrel"
            ]
        }
    },
    {
        id: "double_barrel",
        type: PerkType.Passive,
        name: "Double Barrel",
        description: "[PERMANENT] Shoot 2 bullets side by side",
        icon: 1,
        callback: () => {
            const player = SPPlayer.Singleton;
            player.gun.onShoot = () => {

                const center = player.transform.position.add(player.transform.forward.scale(player.size + 3));

                player.engine.NewGameObjectWithScript(SPBullet, {
                    position: center.add(player.transform.left.scale(4)),
                    initialValues: {
                        damage: player.gun.damage,
                        velocity: player.velocity.add(player.transform.forward.scale(player.gun.bulletSpeed)),
                        maxEnemiesHit: player.gun.maxEnemiesHit,
                        arcBlaster: player.gun.arcBlaster
                    }
                });
                player.engine.NewGameObjectWithScript(SPBullet, {
                    position: center.add(player.transform.right.scale(4)),
                    initialValues: {
                        damage: player.gun.damage,
                        velocity: player.velocity.add(player.transform.forward.scale(player.gun.bulletSpeed)),
                        maxEnemiesHit: player.gun.maxEnemiesHit,
                        arcBlaster: player.gun.arcBlaster
                    }
                });
            }
        },
        onLose: () => {},
        requirements: { level: { min: 6, max: 12 }, excludePerkIds: [
            "tri_shot"
        ] }
    },
    {
        id: "quad_barrel",
        type: PerkType.Passive,
        name: "Quad Barrel",
        description: "[PERMANENT] Shoot 4 bullets side by side",
        icon: 1,
        callback: () => {
            const player = SPPlayer.Singleton;
            player.gun.onShoot = () => {

                const center = player.transform.position.add(player.transform.forward.scale(player.size + 3));

                player.engine.NewGameObjectWithScript(SPBullet, {
                    position: center.add(player.transform.left.scale(12)),
                    initialValues: {
                        damage: player.gun.damage,
                        velocity: player.velocity.add(player.transform.forward.scale(player.gun.bulletSpeed)),
                        maxEnemiesHit: player.gun.maxEnemiesHit,
                        arcBlaster: player.gun.arcBlaster
                    }
                });
                player.engine.NewGameObjectWithScript(SPBullet, {
                    position: center.add(player.transform.left.scale(8)),
                    initialValues: {
                        damage: player.gun.damage,
                        velocity: player.velocity.add(player.transform.forward.scale(player.gun.bulletSpeed)),
                        maxEnemiesHit: player.gun.maxEnemiesHit,
                        arcBlaster: player.gun.arcBlaster
                    }
                });
                player.engine.NewGameObjectWithScript(SPBullet, {
                    position: center.add(player.transform.right.scale(12)),
                    initialValues: {
                        damage: player.gun.damage,
                        velocity: player.velocity.add(player.transform.forward.scale(player.gun.bulletSpeed)),
                        maxEnemiesHit: player.gun.maxEnemiesHit,
                        arcBlaster: player.gun.arcBlaster
                    }
                });
                player.engine.NewGameObjectWithScript(SPBullet, {
                    position: center.add(player.transform.right.scale(8)),
                    initialValues: {
                        damage: player.gun.damage,
                        velocity: player.velocity.add(player.transform.forward.scale(player.gun.bulletSpeed)),
                        maxEnemiesHit: player.gun.maxEnemiesHit,
                        arcBlaster: player.gun.arcBlaster
                    }
                });
            }
        },
        onLose: () => {},
        requirements: { level: { min: 12, max: 20 }, perkId: "double_barrel" }
    },
    // {
    //     id: "nanite_swarm",
    //     type: PerkType.Ability,
    //     name: "Nanite Swarm",
    //     description: "Deploys nanobots to repair allies or damage foes.",
    //     icon: 4,
    //     callback: () => {},
    //     onLose: () => {},
    //     requirements: { level: { min: 6, max: 12 } }
    // },
    {
        id: "evasive_maneuvers",
        type: PerkType.Passive,
        name: "Evasive Maneuvers",
        description: " Grants brief invulnerability after dashing",
        icon: 4,
        callback: () => {
            // const dash: SPDash = SPPlayer.Singleton.gameObject.GetScript(SPDash);
            const dash: SPDash|undefined = SPPlayer.Singleton.GetAbility(SPDash) as SPDash|undefined;

            if (dash) dash.invuln = true;
        },
        onLose: () => {
            // const dash: SPDash = SPPlayer.Singleton.gameObject.GetScript(SPDash);
            const dash: SPDash|undefined = SPPlayer.Singleton.GetAbility(SPDash) as SPDash|undefined;

            if (dash) dash.invuln = false;
        },
        requirements: {
            level: { min: 6, max: 12 },
            perkId: "afterburner_dash"
        }
    },
    {
        id: "plasma_wave",
        type: PerkType.Ability,
        name: "Plasma Wave",
        description: "Unleashes a wave of plasma, damaging all in its path.",
        icon: 2,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new PlasmaWave(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(PlasmaWave);
        },
        requirements: { level: { min: 7, max: 25 } }
    },
    {
        id: "precision_matrix",
        type: PerkType.Passive,
        name: "Precision Matrix",
        description: "Increases critical hit chance by 15%",
        icon: 11,
        callback: () => {
            SPPlayer.Singleton.critChance += 0.15;
        },
        onLose: () => {
            SPPlayer.Singleton.critChance -= 0.15;
            
            if (SPPlayer.Singleton.critChance < 0) SPPlayer.Singleton.critChance = 0;
        },
        requirements: { level: { min: 7, max: 14 } }
    },
    {
        id: "graviton_pulse",
        type: PerkType.Ability,
        name: "Graviton Pulse",
        description: "Manipulates gravity to pull enemies into a vortex.",
        icon: 4,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new GravitonPulse(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(GravitonPulse);
        },
        requirements: { level: { min: 7, max: 14 } }
    },
    {
        id: "stopping_power",
        type: PerkType.Passive,
        name: "Stopping Power",
        description: "Bullets go through 1 more enemy.",
        icon: 4,
        callback: () => {
            SPPlayer.Singleton.gun.maxEnemiesHit++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.maxEnemiesHit--;
        },
        requirements: { level: { min: 7, max: 14 } }
    },
    {
        id: "stopping_power_2",
        type: PerkType.Passive,
        name: "Stopping Power II",
        description: "Bullets go through 1 more enemy.",
        icon: 4,
        callback: () => {
            SPPlayer.Singleton.gun.maxEnemiesHit++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.maxEnemiesHit--;
        },
        requirements: { level: { min: 13, max: 20 }, perkId: "stopping_power" }
    },
    {
        id: "stopping_power_3",
        type: PerkType.Passive,
        name: "Stopping Power III",
        description: "Bullets go through 1 more enemy.",
        icon: 4,
        callback: () => {
            SPPlayer.Singleton.gun.maxEnemiesHit++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.maxEnemiesHit--;
        },
        requirements: { level: { min: 17, max: 30 }, perkId: "stopping_power" }
    },
    // {
    //     id: "cloak_field",
    //     type: PerkType.Ability,
    //     name: "Cloak Field",
    //     description: "Renders the ship invisible for a short duration.",
    //     icon: 6,
    //     callback: () => {},
    //     onLose: () => {},
    //     requirements: { level: { min: 8, max: 15 } }
    // },
    {
        id: "hypercharge",
        type: PerkType.Ability,
        name: "Hypercharge",
        description: "Supercharges weapon systems for increased damage output.",
        icon: 3,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new Hypercharge(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(Hypercharge);
        },
        requirements: { level: { min: 8, max: 15 } }
    },
    // {
    //     id: "shockwave",
    //     type: PerkType.Ability,
    //     name: "Shockwave",
    //     description: "Emits a powerful shockwave, pushing back enemies.",
    //     icon: 13,
    //     callback: () => {},
    //     onLose: () => {},
    //     requirements: { level: { min: 9, max: 16 } }
    // },
    // {
    //     id: "adaptive_armor",
    //     type: PerkType.Ability,
    //     name: "Adaptive Armor",
    //     description: "Adjusts defenses to resist the last type of damage taken.",
    //     icon: 2,
    //     callback: () => {},
    //     onLose: () => {},
    //     requirements: { level: { min: 9, max: 16 } }
    // },
    {
        id: "energy_overload",
        type: PerkType.Ability,
        name: "Energy Overload",
        description: "Temporarily boosts energy regeneration rate.",
        icon: 13,
        callback: () => {},
        onLose: () => {},
        requirements: { level: { min: 10, max: 20 } }
    },
    {
        id: "flak_barrage",
        type: PerkType.Ability,
        name: "Flak Barrage",
        description: "Launches a spread of flak explosions in all directions.",
        icon: 10,
        callback: () => {},
        onLose: () => {},
        requirements: { level: { min: 10, max: 20 } }
    },
    {
        id: "force_field",
        type: PerkType.Ability,
        name: "Force Field",
        description: "Generate a shield that blocks enemy fire",
        icon: 1,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new ForceField(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(ForceField);
        },
        requirements: { level: { min: 11, max: 16 } }
    },
    {
        id: "time_dilation",
        type: PerkType.Ability,
        name: "Time Dilation",
        description: "Slows down time for enemies within a small area.",
        icon: 10,
        callback: () => {},
        onLose: () => {},
        requirements: { level: { min: 11, max: 21 } }
    },
    {
        id: "energy_efficiency",
        type: PerkType.Passive,
        name: "Energy Efficiency",
        description: "Reduces the cooldown of all abilities.",
        icon: 13,
        callback: () => {
            SPPlayer.Singleton.abilities.forEach(ability => {
                ability.cooldown *= 0.9;
            });
        },
        onLose: () => {
            SPPlayer.Singleton.abilities.forEach(ability => {
                ability.cooldown /= 0.9;
            });
        },
        requirements: { level: { min: 6, max: 12 } }
    },
    {
        id: "weapon_mastery",
        type: PerkType.Passive,
        name: "Weapon Mastery",
        description: "Increases weapon damage output.",
        icon: 2,
        callback: () => {
            SPPlayer.Singleton.gun.damage++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.damage--;
        },
        requirements: { level: { min: 7, max: 14 } }
    },
    {
        id: "weapon_mastery_2",
        type: PerkType.Passive,
        name: "Weapon Mastery",
        description: "Increases weapon damage output.",
        icon: 2,
        callback: () => {
            SPPlayer.Singleton.gun.damage++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.damage--;
        },
        requirements: { level: { min: 12, max: 15 } }
    },
    {
        id: "weapon_mastery_3",
        type: PerkType.Passive,
        name: "Weapon Mastery",
        description: "Increases weapon damage output.",
        icon: 2,
        callback: () => {
            SPPlayer.Singleton.gun.damage++;
        },
        onLose: () => {
            SPPlayer.Singleton.gun.damage--;
        },
        requirements: { level: { min: 17, max: 25 } }
    },
    {
        id: "quantum_jump",
        type: PerkType.Ability,
        name: "Quantum Jump",
        description: "Teleports the ship to a targeted location instantly.",
        icon: 11,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new QuantumJump(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(QuantumJump);
        },
        requirements: { level: { min: 12, max: 22 } }
    },
    {
        id: "berserk_mode",
        type: PerkType.Ability,
        name: "Berserk Mode",
        description: "Increases damage output significantly for a short time.",
        icon: 7,
        callback: () => {
            SPPlayer.Singleton.AddAbility(new Berserk(SPPlayer.Singleton));
        },
        onLose: () => {
            SPPlayer.Singleton.RemoveAbility(Berserk);
        },
        requirements: { level: { min: 12, max: 22 } }
    }
];

export function GetPerkById(id: string) {
    for (const perk of PERKS) {
        if (perk.id === id) return perk;
    }
}

export function GetAbilitySelection(level: number) {
    const takenIds = SPGameManager.Singleton.takenAbilityIds;
    const taken = Object.values(SPGameManager.Singleton.takenAbilities)

    let valid = [...PERKS];

    if (taken.filter(v => v.type === PerkType.Ability).length >= 10) {
        //max abilities taken, restrict valids
        valid = valid.filter(v => v.type !== PerkType.Ability);
    }

    valid = valid.filter(
        v => !takenIds.includes(v.id) && level >= v.requirements.level.min && level <= v.requirements.level.max &&
        (!v.requirements.perkId || takenIds.includes(v.requirements.perkId)) &&
        (!v.requirements.excludePerkIds || ContainsNone(takenIds, v.requirements.excludePerkIds))
    );

    if ([1,5,11,16].includes(level)) {
        valid = valid.filter(
            v => v.type === PerkType.Weapon
        );
    } else {
        valid = valid.filter(v => v.type !== PerkType.Weapon)
    }

    if (valid.length < 1) return [];

    console.log(valid);

    const sel: Perk[] = [];
    const usedR: number[] = [];
    let r: number;

    for (let i = 0; i < (valid.length < 3 ? valid.length : 3); i++) {
        do {
            r = Random.int(0, valid.length);
        } while (usedR.includes(r));
        sel.push(valid[r]);
        usedR.push(r);
    }

    return sel;
}